import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Text, useDebouncedFn } from "@jsq/ds/core";
import { useFormContext, FormTextInput } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { PaymentsRoutingNumberType } from "#graphql/supergraphEnums";
import { useValidateRoutingNumberLazyQuery } from "#graphql/supergraphHooks";
import { type ValidateRoutingNumberQuery } from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { CountrySelectionDropDown } from "../../../../../common/components/CountrySelectionDropDown";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { useConsolidatedPaymentPrefContext } from "../../../Context";
import { useGenericBankValidation } from "../../../hooks/use-generic-bank-validation";
import { getCountryValue, getNameFromCountry } from "../../../mappers";
import { BankCountryMismatchWarning } from "../../BankCountryMismatchWarning";
import { type SepaPaymentPrefInputType } from "../constants";
import { isSepaCountry } from "../sepaCountries";
import { normalizeSwiftCode } from "../utils";
import { BankDetailsInputFields, SepaBankDetailsInputSchema } from "./constants";

export const BankDetails: React.FC = () => {
  const { bankCountryLocked } = useConsolidatedPaymentPrefContext();
  // Lock only when the country was set by an upstream source (country-picker or edit-hydration),
  // not when it was echoed back by this form's own submit (fixes FF-7684).
  const isBankCountryLocked: boolean = bankCountryLocked;
  const [swiftField] = useField<string>(BankDetailsInputFields.BicSwiftCode);
  const [bankCountryField] = useField<string>(BankDetailsInputFields.BankCountry);
  const { setFieldTouched } = useFormContext();
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);

  const { handleClear, handleValidateCompleted } =
    useGenericBankValidation<SepaPaymentPrefInputType>({
      nameField: BankDetailsInputFields.BankName,
      validatedCountryField: BankDetailsInputFields.BankCountryValidatedCountry,
    });

  useEffect(() => {
    if (!swiftField.value) {
      handleClear();
    }
  }, [swiftField.value, handleClear]);

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data: ValidateRoutingNumberQuery) => {
      const currentCleanCode = normalizeSwiftCode(swiftField.value);
      const responseRoutingNumber = normalizeSwiftCode(
        data?.validateRoutingNumber?.routingNumber,
      );

      if (currentCleanCode !== responseRoutingNumber) {
        return;
      }

      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};
      handleValidateCompleted({ setShowBankValidatorError, bankName, bankAddress, isValid });
    },
    context: clientContext(),
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidateRoutingNumber = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    const cleanCode = normalizeSwiftCode(swiftField.value);

    if (!cleanCode) {
      handleClear();
      setShowBankValidatorError(false);
      return;
    }

    const isValid = SepaBankDetailsInputSchema.pick([
      BankDetailsInputFields.BicSwiftCode,
    ]).isValidSync({
      [BankDetailsInputFields.BicSwiftCode]: cleanCode,
    });

    if (isValid) {
      debouncedValidateRoutingNumber({
        variables: {
          validateRoutingNumberInput: {
            routingNumber: cleanCode,
            routingNumberType: PaymentsRoutingNumberType.Swift,
          },
        },
      });
    } else {
      handleClear();
      setShowBankValidatorError(false);
    }
  }, [swiftField.value, debouncedValidateRoutingNumber, handleClear]);

  return (
    <FieldGroup title="Bank details">
      <MultiInputFormRow>
        <CountrySelectionDropDown
          name={BankDetailsInputFields.BankCountry}
          label="Bank country"
          getValue={getCountryValue}
          disabled={isBankCountryLocked}
          filterCountries={(country) => isSepaCountry(country.code)}
        />
        <div />
      </MultiInputFormRow>
      <MultiInputFormRow>
        <Flex flexDirection="column">
          <FormTextInput
            isFullWidth
            name={BankDetailsInputFields.BicSwiftCode}
            onPaste={() => {
              void setFieldTouched(BankDetailsInputFields.BicSwiftCode, true, true);
            }}
            label={
              <HelpIndicator
                id="bic-swift-code-help-indicator"
                placement="right"
                data-cy="help-icon"
                content="An 8 or 11-character code that identifies your bank internationally. It can be found on your bank statement or in your online banking portal."
              >
                <Text>
                  SWIFT/BIC code <Icon type="question-circle" size="small" variant="outline" />
                </Text>
              </HelpIndicator>
            }
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized Swift code</ErrorMessage>}
        </Flex>
        <FormTextInput label="Bank name" isFullWidth name={BankDetailsInputFields.BankName} />
      </MultiInputFormRow>
      <BankCountryMismatchWarning
        bankCountryName={getNameFromCountry(bankCountryField.value)}
        validatedCountryField={BankDetailsInputFields.BankCountryValidatedCountry}
      />
    </FieldGroup>
  );
};
